/**
 * Appointments Page
 * Main page for appointment management with calendar and list views
 */

import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  useAppointmentsQuery,
  type AppointmentFilters,
  type AppointmentWithDetails,
} from '../hooks/useAppointments';
import { Calendar } from '../components/appointments/Calendar';
import { AppointmentCard } from '../components/appointments/AppointmentCard';
import { AppointmentForm } from '../components/appointments/AppointmentForm';
import { AppointmentDetail } from '../components/appointments/AppointmentDetail';
import { getDateFromISO } from '../utils/calendar';
import type { AppointmentStatus } from '../types';

type ViewMode = 'calendar' | 'list';

export function AppointmentsPage() {
  const { provider } = useAuth();
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [showNewForm, setShowNewForm] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<
    AppointmentWithDetails | undefined
  >();
  const [newAppointmentDate, setNewAppointmentDate] = useState<string>('');
  const [newAppointmentTime, setNewAppointmentTime] = useState<string>('');

  // Filters
  const [statusFilter, setStatusFilter] = useState<AppointmentStatus | 'all'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Build filter object
  const filters: AppointmentFilters = {
    status: statusFilter !== 'all' ? statusFilter : undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    limit: 200,
  };

  // Queries
  const { data: appointmentsData, isLoading, refetch } = useAppointmentsQuery(filters);

  const appointments = appointmentsData?.data || [];
  const totalAppointments = appointmentsData?.meta?.total || 0;

  // Handlers
  const handleNewAppointment = (date?: string, time?: string) => {
    if (date) setNewAppointmentDate(date);
    if (time) setNewAppointmentTime(time);
    setShowNewForm(true);
  };

  const handleAppointmentClick = (appointment: AppointmentWithDetails) => {
    setSelectedAppointment(appointment);
  };

  const handleDateClick = (date: Date) => {
    const dateStr = getDateFromISO(date.toISOString());
    handleNewAppointment(dateStr);
  };

  const canSchedule = provider?.permissions.schedule ?? false;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tímabókanir</h1>
            <p className="text-sm text-gray-600 mt-1">
              {totalAppointments} tímabókanir í heildina
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* View mode toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'calendar'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'list'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 10h16M4 14h16M4 18h16"
                  />
                </svg>
              </button>
            </div>

            {/* New appointment button */}
            {canSchedule && (
              <button
                onClick={() => handleNewAppointment()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Ný tímabókun
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 bg-white rounded-lg shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Status filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Staða</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as AppointmentStatus | 'all')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Allar stöður</option>
              <option value="scheduled">Áætlaður</option>
              <option value="confirmed">Staðfestur</option>
              <option value="arrived">Mættur</option>
              <option value="in_progress">Í gangi</option>
              <option value="completed">Lokið</option>
              <option value="cancelled">Afbókað</option>
              <option value="no_show">Mætti ekki</option>
            </select>
          </div>

          {/* Date from */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Frá dagsetningu</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Date to */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Til dagsetningar</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Clear filters */}
          <div className="flex items-end">
            <button
              onClick={() => {
                setStatusFilter('all');
                setDateFrom('');
                setDateTo('');
              }}
              className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Hreinsa síur
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500">Hleð tímabókunum...</div>
          </div>
        ) : viewMode === 'calendar' ? (
          <Calendar
            appointments={appointments}
            onAppointmentClick={handleAppointmentClick}
            onTimeSlotClick={handleNewAppointment}
            onDateClick={handleDateClick}
          />
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden h-full">
            <div className="overflow-y-auto h-full p-4">
              {appointments.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-500">
                  Engar tímabókanir fundust
                </div>
              ) : (
                <div className="space-y-3">
                  {appointments.map((appointment) => (
                    <AppointmentCard
                      key={appointment.id}
                      appointment={appointment}
                      onClick={() => handleAppointmentClick(appointment)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* New appointment form modal */}
      {showNewForm && (
        <AppointmentForm
          initialDate={newAppointmentDate}
          initialTime={newAppointmentTime}
          onClose={() => {
            setShowNewForm(false);
            setNewAppointmentDate('');
            setNewAppointmentTime('');
          }}
          onSuccess={() => {
            refetch();
          }}
        />
      )}

      {/* Appointment detail modal */}
      {selectedAppointment && (
        <AppointmentDetail
          appointment={selectedAppointment}
          onClose={() => setSelectedAppointment(undefined)}
          onRefresh={() => {
            refetch();
          }}
        />
      )}
    </div>
  );
}
